/**
 * Provisional: a local grouped combobox, people then company attribute values under type headings.
 * To be replaced by the grouped list of `@amelio/ui-library`; kept inside the objectives module
 * until then.
 */
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ObjectiveEmployeeOptionList from './ObjectiveEmployeeOptionList';
import ObjectiveEmployeePickerNotices from './ObjectiveEmployeePickerNotices';
import ObjectiveEmployeeSearchField from './ObjectiveEmployeeSearchField';
import ObjectiveSelectedParticipants from './ObjectiveSelectedParticipants';
import SectionHeading from './SectionHeading';
import { useEmployeeSearchList } from '../hooks/useEmployeeSearchList';
import { useOwnerHandover } from '../hooks/useOwnerHandover';
import { useParticipantAttributeGroups } from '../hooks/useParticipantAttributeGroups';
import {
    filterParticipantAttributeGroups,
    isSameParticipantAttribute,
} from '../participantAttributes';

import type { User } from '@/types/directory/User';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';

import { useGetAllCompanyOrTeamUsers, useGetCompanyUsers, useGetUsersByIds } from '@/api/directory';
import { useResolveParticipantAttributes } from '@/api/objectives';
import { getFullName } from '@/utils/user';

const SEARCH_PAGE_SIZE = 20;

/**
 * Where the first pick, the owner, is searched: the whole company, or the reader's whole reporting
 * line -- direct and indirect reports, and the reader too when they may own an objective. Every
 * later pick is a participant, who may be anyone in the company.
 */
type OwnerCandidates = 'company' | 'reportingLine' | 'reportingLineAndSelf';

export interface ObjectiveEmployeePickerProps {
    selectedEmployeeIds: string[];
    onChange: (employeeIds: string[]) => void;
    /** Cosmetic only: changes the placeholder text. Any number of employees can be added
     * regardless of mode — that is what triggers the individual→team auto-switch in
     * `ObjectiveFormPage`. */
    mode: 'simple' | 'multiple';
    ownerCandidates: OwnerCandidates;
    /** The owner, first in the list, cannot be removed: someone who only manages their own
     * objectives neither hands one over nor takes one from a colleague. Anyone else may remove the
     * owner, and the objective passes to the next employee within their reach. */
    isOwnerLocked?: boolean;
    /** The attribute values whose members take part, shown as chips after the people. */
    selectedAttributes?: ObjectiveParticipantAttribute[];
    onAttributesChange?: (attributes: ObjectiveParticipantAttribute[]) => void;
    /** Whether attribute values are offered, and their chips removable: only to someone who manages
     * other people's objectives. An attribute value never lands among the employees, so it can
     * never become the owner; what the field offers therefore never depends on what is picked. */
    canPickAttributes?: boolean;
    /** The objective being edited: its own attributes are counted within the reach they were added
     * under, for anyone who may edit it. */
    objectiveId?: string;
    error?: boolean;
    errorMessage?: string;
}

export default function ObjectiveEmployeePicker({
    selectedEmployeeIds,
    onChange,
    mode,
    ownerCandidates,
    isOwnerLocked = false,
    selectedAttributes = [],
    onAttributesChange,
    canPickAttributes = false,
    objectiveId,
    error,
    errorMessage,
}: ObjectiveEmployeePickerProps) {
    const { t } = useTranslation();
    const search = useEmployeeSearchList();
    const { debouncedSearch, isListOpen } = search;

    const { data: selectedUsers = [] } = useGetUsersByIds({
        userIds: selectedEmployeeIds,
        identifier: 'objective-employee-picker',
    });
    const isReachLimited = ownerCandidates !== 'company';
    // A reader whose reach is their team picks everyone, owner and participants alike, within it:
    // the same people whether the form opened empty or with the reader already set as the owner.
    const searchesReportingLine = isReachLimited;

    // The whole reporting line, indirect reports included, as the server reaches it; it is read once
    // and narrowed here, since the directory's search only knows the direct reports. It also says
    // who may receive the objective when its owner is removed.
    const { data: reportingLine, isLoading: isReportingLineLoading } = useGetAllCompanyOrTeamUsers({
        enabled: isReachLimited,
        canManageTeamPerformance: true,
        includeSelf: ownerCandidates === 'reportingLineAndSelf',
    });
    const { data: companySearch, isLoading: isCompanySearchLoading } = useGetCompanyUsers(
        {
            pageNumber: 1,
            pageSize: SEARCH_PAGE_SIZE,
            searchQuery: debouncedSearch,
            isActiveOnly: true,
            includeInvited: true,
        },
        { enabled: isListOpen && !searchesReportingLine },
    );

    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    const candidates = searchesReportingLine
        ? (reportingLine ?? [])
              .filter((employee) => getFullName(employee).toLowerCase().includes(normalizedSearch))
              .slice(0, SEARCH_PAGE_SIZE)
        : (companySearch?.items ?? []);
    const isSearchLoading = searchesReportingLine ? isReportingLineLoading : isCompanySearchLoading;
    const searchOptions = candidates.filter(
        (employee) => !selectedEmployeeIds.includes(employee.id),
    );

    // The company is read for the chips too, so an objective carrying attributes names them even
    // for someone who may not add one.
    const {
        groups: attributeGroups,
        attributesByKey,
        choosableGroups,
        isError: isAttributeLoadError,
        isChoosableError,
    } = useParticipantAttributeGroups({
        enabled: canPickAttributes || selectedAttributes.length > 0,
        teamsWithinReach: canPickAttributes && ownerCandidates !== 'company',
    });
    const { data: attributeResolution } = useResolveParticipantAttributes({
        attributes: selectedAttributes,
        objectiveId,
        enabled: canPickAttributes || !!objectiveId,
    });
    const attributeOptionGroups =
        canPickAttributes && choosableGroups
            ? filterParticipantAttributeGroups(choosableGroups, debouncedSearch, selectedAttributes)
            : [];
    const areAttributesLoading = canPickAttributes && !choosableGroups && !isChoosableError;

    // Everyone this picker has laid eyes on, kept across renders. The chips are drawn from here,
    // never straight from the query: that query is keyed by the very list of ids being changed, so
    // adding a person asks the server about a set it was never asked about — an empty cache entry
    // with no data — and every chip would vanish until the round trip landed.
    // What the user just picked is already on the client: they picked it from the list below.
    const knownUsersRef = useRef(new Map<string, User>());
    [...selectedUsers, ...searchOptions].forEach((user) =>
        knownUsersRef.current.set(user.id, user),
    );
    const knownUsers = knownUsersRef.current;

    // The server refuses an owner outside the reader's reach. Until the reach is read, nobody is
    // known to be within it.
    const reachedIds = useMemo(
        () => new Set(reportingLine?.map((employee) => employee.id)),
        [reportingLine],
    );
    const isWithinReach = (employeeId: string) => !isReachLimited || reachedIds.has(employeeId);

    const { isOwnerKept, changeEmployees, removeEmployee, isRemovable } = useOwnerHandover({
        selectedEmployeeIds,
        onChange,
        isOwnerLocked,
        isWithinReach,
    });

    const addEmployee = (employeeId: string) => {
        changeEmployees([...selectedEmployeeIds, employeeId]);
        search.clear();
    };

    const addAttribute = (attribute: ObjectiveParticipantAttribute) => {
        onAttributesChange?.([...selectedAttributes, attribute]);
        search.clear();
    };

    const removeAttribute = (attribute: ObjectiveParticipantAttribute) => {
        onAttributesChange?.(
            selectedAttributes.filter(
                (candidate) => !isSameParticipantAttribute(candidate, attribute),
            ),
        );
    };

    // The chips are drawn from what this picker has already laid eyes on; an id it has never
    // seen a user for shows nothing rather than an empty chip.
    const selectedParticipants = selectedEmployeeIds
        .map((employeeId, index) => ({
            user: knownUsers.get(employeeId),
            isRemovable: isRemovable(employeeId, index),
        }))
        .filter((entry): entry is { user: User; isRemovable: boolean } => !!entry.user);

    // Nothing is said about a value before the company has answered; a failed read is an answer.
    const selectedAttributeChips =
        attributeGroups || isAttributeLoadError
            ? selectedAttributes.map((attribute) => ({
                  attribute,
                  employeeCount: attributeResolution?.attributes.find((resolved) =>
                      isSameParticipantAttribute(resolved, attribute),
                  )?.employeeCount,
              }))
            : [];
    // Zero, not unknown: a value still being counted says nothing yet.
    const emptyAttributes = canPickAttributes
        ? selectedAttributeChips
              .filter(({ employeeCount }) => employeeCount === 0)
              .map(({ attribute }) => attribute)
        : [];

    // Both the section heading and the results list share this label: "Sélectionner un employé"
    // for one, "Sélectionner les employés" for several -- the mode chosen for the field itself.
    const selectEmployeeLabel = t(
        mode === 'simple' ? 'objectives.form.selectEmployee' : 'objectives.form.selectEmployees',
    );

    return (
        <div
            className={'flex flex-col gap-3'}
            data-testid={'objective-employee-picker'}
        >
            <SectionHeading
                icon={'users-user-search'}
                title={selectEmployeeLabel}
            />

            <ObjectiveEmployeeSearchField
                search={search}
                label={selectEmployeeLabel}
                placeholder={selectedEmployeeIds.length === 0 ? selectEmployeeLabel : ''}
                hasError={error}
                selection={
                    <ObjectiveSelectedParticipants
                        employees={selectedParticipants}
                        onRemoveEmployee={removeEmployee}
                        attributes={selectedAttributeChips}
                        attributesByKey={attributesByKey}
                        onRemoveAttribute={canPickAttributes ? removeAttribute : undefined}
                    />
                }
            >
                <ObjectiveEmployeeOptionList
                    employees={searchOptions}
                    areEmployeesLoading={isSearchLoading}
                    attributeGroups={attributeOptionGroups}
                    areAttributesLoading={areAttributesLoading}
                    hasAttributeLoadError={canPickAttributes && isChoosableError}
                    onPickEmployee={addEmployee}
                    onPickAttribute={addAttribute}
                />
            </ObjectiveEmployeeSearchField>

            <ObjectiveEmployeePickerNotices
                isOwnerKept={isOwnerKept}
                emptyAttributes={emptyAttributes}
                attributesByKey={attributesByKey}
                errorMessage={error ? errorMessage : undefined}
            />
        </div>
    );
}
